import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

import Tracking from "assets/images/Tracking.png";
import FlexBetween from "components/FlexBetween";
import SnackbarComponent from "components/SnackbarComponent";
import { resetPassword } from "reduxStore/actions/authActions";
import { setSnackbar } from "reduxStore/state";

const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(25, "Password must be at most 25 characters")
    .required("Password is required")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&-])[A-Za-z\d@$!%*?&-]{6,25}$/,
      "Password must include at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&-)."
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const validatePassword = (password) => {
  return {
    hasMinLength: password.length >= 6,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[@$!%*?&-]/.test(password),
  };
};

function ResetPasswordForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useParams(); // The token from the URL

  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const [passwordChecklist, setPasswordChecklist] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const handlePasswordChange = (password) => {
    const checklist = validatePassword(password);
    setPasswordChecklist(checklist);
  };

  const handleResetPassword = async (values) => {
    try {
      const response = await dispatch(resetPassword(token, values.password));

      if (response.success) {
        dispatch(
          setSnackbar({
            open: true,
            message:
              "Password reset successfully. You will now be redirected to the login page.",
            severity: "success",
          })
        );
        setTimeout(() => {
          navigate("/");
        }, 4000);
      } else {
        dispatch(
          setSnackbar({
            open: true,
            message: response.message || "Failed to reset password.",
            severity: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message:
            error.message || "Error resetting password. Please try again.",
          severity: "error",
        })
      );
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const passwordTooltip =
    "Password must be 6-25 characters long and include at least one uppercase letter, one lowercase letter, one digit, and one of the following special characters: @$!%*?&-";

  return (
    <Box
      sx={{
        backgroundImage: `url(${Tracking})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        width="100%"
        backgroundColor="rgba(255, 255, 255, 0.8)"
        p="1rem 6%"
        textAlign="center"
      >
        <Typography color="primary" variant="h1">
          ALT Coach
        </Typography>
      </Box>
      <Box
        width={isLargeScreen ? "50%" : "93%"}
        p="2rem"
        m="2rem auto"
        borderRadius="1.5rem"
        backgroundColor="rgba(255, 255, 255, 0.9)"
      >
        <Typography fontWeight="500" variant="h5" sx={{ mb: "1.5rem" }}>
          Reset Your Password
        </Typography>
        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={resetPasswordSchema}
          onSubmit={handleResetPassword}
        >
          {({ values, errors, touched, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Box>
                <Tooltip title={passwordTooltip} arrow placement="right-start">
                  <TextField
                    label="New Password"
                    name="password"
                    value={values.password}
                    onChange={(e) => {
                      handleChange(e);
                      handlePasswordChange(e.target.value);
                    }}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{
                              backgroundColor: "transparent",
                              paddingRight: "1rem",
                            }}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlined />
                            ) : (
                              <VisibilityOutlined />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
                <TextField
                  label="Confirm New Password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  error={
                    touched.confirmPassword && Boolean(errors.confirmPassword)
                  }
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  margin="normal"
                />
                <FlexBetween>
                  {/* Checklist */}
                  <List>
                    <ListItem sx={{ paddingTop: 0, paddingBottom: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={passwordChecklist.hasMinLength}
                            color="primary"
                          />
                        }
                        label="At least 6 characters"
                      />
                    </ListItem>
                    <ListItem sx={{ paddingTop: 0, paddingBottom: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={passwordChecklist.hasUppercase}
                            color="primary"
                          />
                        }
                        label="At least one uppercase letter"
                      />
                    </ListItem>
                    <ListItem sx={{ paddingTop: 0, paddingBottom: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={passwordChecklist.hasLowercase}
                            color="primary"
                          />
                        }
                        label="At least one lowercase letter"
                      />
                    </ListItem>
                    <ListItem sx={{ paddingTop: 0, paddingBottom: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={passwordChecklist.hasNumber}
                            color="primary"
                          />
                        }
                        label="At least one number"
                      />
                    </ListItem>
                    <ListItem sx={{ paddingTop: 0, paddingBottom: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={passwordChecklist.hasSpecialChar}
                            color="primary"
                          />
                        }
                        label="At least one special character (@$!%*?&-)"
                      />
                    </ListItem>
                  </List>

                  <Button type="submit" variant="contained" color="primary">
                    Reset Password
                  </Button>
                </FlexBetween>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
      <SnackbarComponent />
    </Box>
  );
}

export default ResetPasswordForm;
